import { useEffect, useState, type RefObject } from 'react'
import { merge, fromEvent, Subject, map } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { useRefDep } from './ref-dep'

/**
 * Manages hover state for a ref.
 *
 * @param ref
 * @param onHoverChange
 */
export function useHoverState(
  ref: RefObject<HTMLElement | null>,
  onHoverChange?: (isHovering: boolean) => unknown,
) {
  const [isHovering, setIsHovering] = useState(false)
  useEffect(() => {
    if (!ref.current) {
      return
    }
    const unsubscribing = new Subject<boolean>()
    const isHovering$ = merge(
      fromEvent(ref.current, 'mouseenter').pipe(map(() => true)),
      fromEvent(ref.current, 'mouseleave').pipe(map(() => false)),
      unsubscribing,
    ).pipe(distinctUntilChanged())

    const subscription = isHovering$.subscribe((value) => {
      setIsHovering(value)
      if (onHoverChange) {
        onHoverChange(value)
      }
    })
    return () => {
      unsubscribing.next(false)
      unsubscribing.complete()
      subscription.unsubscribe()
    }
  }, [onHoverChange, useRefDep(ref)])

  return isHovering
}
